import React, { useState, useMemo } from 'react';
import { bulkUpdateMembers } from '../services/apiService';

const YEAR_PROMOTION_MAP = {
  '1st Year': '2nd Year',
  '2nd Year': '3rd Year',
  '3rd Year': '4th Year',
  '4th Year': 'Alumni',
  '1st': '2nd Year',
  '2nd': '3rd Year',
  '3rd': '4th Year',
  '4th': 'Alumni',
};

const normalizeYear = (year) => {
  if (!year) return 'Unassigned';
  const str = String(year).toLowerCase().trim();
  if (str.includes('1')) return '1st Year';
  if (str.includes('2')) return '2nd Year';
  if (str.includes('3')) return '3rd Year';
  if (str.includes('4')) return '4th Year';
  if (str.includes('alumni') || str.includes('graduat')) return 'Alumni';
  return year;
};

export default function YearPromotionModal({
  isOpen,
  onClose,
  members = [],
  isDarkMode = false,
  onSuccess = () => {},
}) {
  const [selectedBatch, setSelectedBatch] = useState('2nd Year'); // Default to 2nd Year since user mentioned it
  const [selectedMemberIds, setSelectedMemberIds] = useState(new Set());
  const [isExecuting, setIsExecuting] = useState(false);
  const [progressState, setProgressState] = useState({ current: 0, total: 0, memberName: '' });
  const [executionResult, setExecutionResult] = useState(null);

  // Group members by year count
  const membersByYear = useMemo(() => {
    const map = {
      '1st Year': [],
      '2nd Year': [],
      '3rd Year': [],
      '4th Year': [],
      'Alumni': [],
      'Unassigned': [],
    };

    members.forEach((m) => {
      const norm = normalizeYear(m.year);
      if (map[norm]) {
        map[norm].push(m);
      } else {
        if (!map['Unassigned']) map['Unassigned'] = [];
        map['Unassigned'].push(m);
      }
    });

    return map;
  }, [members]);

  // Candidates for promotion based on selected batch
  const candidateMembers = useMemo(() => {
    return members
      .map((m) => {
        const norm = normalizeYear(m.year);
        const nextYear = YEAR_PROMOTION_MAP[norm] || YEAR_PROMOTION_MAP[m.year] || null;
        return {
          ...m,
          currentNormYear: norm,
          nextYear: nextYear,
        };
      })
      .filter((m) => {
        if (!m.nextYear) return false;
        if (selectedBatch === 'all') return true;
        return m.currentNormYear === selectedBatch;
      });
  }, [members, selectedBatch]);

  // Initialize selection when switching batch
  const handleBatchChange = (batch) => {
    setSelectedBatch(batch);
    setExecutionResult(null);
    const candidates = members
      .map((m) => ({ id: m.id || m._id, norm: normalizeYear(m.year) }))
      .filter((m) => batch === 'all' || m.norm === batch);
    setSelectedMemberIds(new Set(candidates.map((c) => c.id)));
  };

  // Toggle selection on individual member
  const handleToggleMember = (id) => {
    setSelectedMemberIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // Select all / Deselect all
  const handleToggleSelectAll = () => {
    const allIds = candidateMembers.map((m) => m.id || m._id);
    const allSelected = allIds.every((id) => selectedMemberIds.has(id));
    if (allSelected) {
      setSelectedMemberIds(new Set());
    } else {
      setSelectedMemberIds(new Set(allIds));
    }
  };

  // Execute promotion
  const handleExecutePromotion = async () => {
    const targets = candidateMembers
      .filter((m) => selectedMemberIds.has(m.id || m._id))
      .map((m) => ({
        id: m.id || m._id,
        name: m.name || m.fullname,
        updates: {
          year: m.nextYear,
        },
      }));

    if (targets.length === 0) {
      alert('Please select at least one member to promote.');
      return;
    }

    if (
      !window.confirm(
        `Are you sure you want to promote ${targets.length} member(s)? Their academic year will advance.`
      )
    ) {
      return;
    }

    setIsExecuting(true);
    setProgressState({ current: 0, total: targets.length, memberName: '' });

    try {
      const result = await bulkUpdateMembers(targets, (current, total, memberName) => {
        setProgressState({ current, total, memberName });
      });

      setExecutionResult(result);
      if (result.successful.length > 0) {
        onSuccess(result);
      }
    } catch (err) {
      alert(`Promotion encountered an error: ${err.message}`);
    } finally {
      setIsExecuting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className={`promotion-modal-overlay ${isDarkMode ? 'dark-mode' : ''}`}>
      <div className="promotion-modal-card">
        {/* Modal Header */}
        <div className="promotion-modal-header">
          <div className="promotion-title-row">
            <div className="promotion-icon-pill">
              <i className="fas fa-graduation-cap"></i>
            </div>
            <div>
              <h3>Academic Year Promotion</h3>
              <p>Promote members to their next academic year in bulk</p>
            </div>
          </div>
          <button
            type="button"
            className="promotion-close-btn"
            onClick={onClose}
            disabled={isExecuting}
            aria-label="Close"
          >
            <i className="fas fa-times"></i>
          </button>
        </div>

        {/* Modal Body */}
        <div className="promotion-modal-body">
          {/* Realtime Progress Bar */}
          {isExecuting && (
            <div className="promotion-progress-box">
              <div className="progress-status-row">
                <i className="fas fa-spinner fa-spin"></i>
                <span>
                  Promoting members ({progressState.current} of {progressState.total})...
                </span>
                <span className="progress-pct">
                  {Math.round((progressState.current / (progressState.total || 1)) * 100)}%
                </span>
              </div>
              <div className="progress-track">
                <div
                  className="progress-fill"
                  style={{
                    width: `${Math.round(
                      (progressState.current / (progressState.total || 1)) * 100
                    )}%`,
                  }}
                ></div>
              </div>
              {progressState.memberName && (
                <small className="progress-member-name">Updating: {progressState.memberName}</small>
              )}
            </div>
          )}

          {/* Result Banner */}
          {executionResult && !isExecuting && (
            <div
              className={`promotion-result-banner ${
                executionResult.failed.length === 0 ? 'success' : 'warning'
              }`}
            >
              <i
                className={`fas ${
                  executionResult.failed.length === 0 ? 'fa-check-circle' : 'fa-info-circle'
                }`}
              ></i>
              <div>
                <strong>
                  {executionResult.failed.length === 0
                    ? `🎉 Successfully promoted ${executionResult.successful.length} member(s)!`
                    : `Promoted ${executionResult.successful.length} of ${executionResult.total} members.`}
                </strong>
                {executionResult.failed.length > 0 && (
                  <p>{executionResult.failed.length} member(s) failed. Check permissions.</p>
                )}
              </div>
            </div>
          )}

          {/* Batch Selector Cards */}
          <div className="promotion-batch-grid">
            <button
              type="button"
              className={`batch-card ${selectedBatch === '2nd Year' ? 'active' : ''}`}
              onClick={() => handleBatchChange('2nd Year')}
              disabled={isExecuting}
            >
              <div className="batch-label">2nd Year → 3rd Year</div>
              <div className="batch-count">{membersByYear['2nd Year']?.length || 0}</div>
              <div className="batch-sub">2nd Years</div>
            </button>

            <button
              type="button"
              className={`batch-card ${selectedBatch === '1st Year' ? 'active' : ''}`}
              onClick={() => handleBatchChange('1st Year')}
              disabled={isExecuting}
            >
              <div className="batch-label">1st Year → 2nd Year</div>
              <div className="batch-count">{membersByYear['1st Year']?.length || 0}</div>
              <div className="batch-sub">1st Years</div>
            </button>

            <button
              type="button"
              className={`batch-card ${selectedBatch === '3rd Year' ? 'active' : ''}`}
              onClick={() => handleBatchChange('3rd Year')}
              disabled={isExecuting}
            >
              <div className="batch-label">3rd Year → 4th Year</div>
              <div className="batch-count">{membersByYear['3rd Year']?.length || 0}</div>
              <div className="batch-sub">3rd Years</div>
            </button>

            <button
              type="button"
              className={`batch-card ${selectedBatch === '4th Year' ? 'active' : ''}`}
              onClick={() => handleBatchChange('4th Year')}
              disabled={isExecuting}
            >
              <div className="batch-label">4th Year → Alumni</div>
              <div className="batch-count">{membersByYear['4th Year']?.length || 0}</div>
              <div className="batch-sub">Graduating</div>
            </button>

            <button
              type="button"
              className={`batch-card ${selectedBatch === 'all' ? 'active' : ''}`}
              onClick={() => handleBatchChange('all')}
              disabled={isExecuting}
            >
              <div className="batch-label">Promote All Batches</div>
              <div className="batch-count">
                {(membersByYear['1st Year']?.length || 0) +
                  (membersByYear['2nd Year']?.length || 0) +
                  (membersByYear['3rd Year']?.length || 0) +
                  (membersByYear['4th Year']?.length || 0)}
              </div>
              <div className="batch-sub">All Eligible</div>
            </button>
          </div>

          {/* Candidate Preview List */}
          <div className="promotion-table-box">
            <div className="promotion-table-header">
              <label className="select-all-label">
                <input
                  type="checkbox"
                  checked={
                    candidateMembers.length > 0 &&
                    candidateMembers.every((m) => selectedMemberIds.has(m.id || m._id))
                  }
                  onChange={handleToggleSelectAll}
                  disabled={isExecuting || candidateMembers.length === 0}
                />
                <span>Select All ({candidateMembers.length})</span>
              </label>

              <span className="selected-count-badge">
                {candidateMembers.filter((m) => selectedMemberIds.has(m.id || m._id)).length} selected
              </span>
            </div>

            <div className="promotion-table-scroll">
              {candidateMembers.length === 0 ? (
                <div className="promotion-empty-state">
                  <i className="fas fa-user-graduate"></i>
                  <p>No members currently in {selectedBatch}.</p>
                </div>
              ) : (
                <table className="promotion-table">
                  <thead>
                    <tr>
                      <th style={{ width: '40px' }}></th>
                      <th>Member</th>
                      <th>Team</th>
                      <th>Current Year</th>
                      <th></th>
                      <th>Promoted Year</th>
                    </tr>
                  </thead>
                  <tbody>
                    {candidateMembers.map((m) => {
                      const mId = m.id || m._id;
                      const isChecked = selectedMemberIds.has(mId);
                      return (
                        <tr
                          key={mId}
                          className={isChecked ? 'checked' : ''}
                          onClick={() => handleToggleMember(mId)}
                        >
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={isChecked}
                              onChange={() => handleToggleMember(mId)}
                              disabled={isExecuting}
                            />
                          </td>
                          <td>
                            <div className="member-meta">
                              <strong>{m.name || m.fullname}</strong>
                              <small>{m.email || m.rollNumber || m.roll_no || ''}</small>
                            </div>
                          </td>
                          <td>
                            <span className="team-badge-pill">{m.team || 'None'}</span>
                          </td>
                          <td>
                            <span className="curr-year-pill">{m.currentNormYear}</span>
                          </td>
                          <td style={{ textAlign: 'center', color: '#6366f1' }}>
                            <i className="fas fa-arrow-right"></i>
                          </td>
                          <td>
                            <span className="next-year-pill">{m.nextYear}</span>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>

        {/* Modal Footer */}
        <div className="promotion-modal-footer">
          <button
            type="button"
            className="promotion-cancel-btn"
            onClick={onClose}
            disabled={isExecuting}
          >
            Cancel
          </button>

          <button
            type="button"
            className="promotion-submit-btn"
            onClick={handleExecutePromotion}
            disabled={
              isExecuting ||
              candidateMembers.filter((m) => selectedMemberIds.has(m.id || m._id)).length === 0
            }
          >
            <i className="fas fa-graduation-cap"></i>
            <span>
              Promote ({candidateMembers.filter((m) => selectedMemberIds.has(m.id || m._id)).length})
              Members
            </span>
          </button>
        </div>
      </div>
    </div>
  );
}
